import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { GetAllExpeditionResponseDtoExpedition } from "@/types/api/api-types";
import TrashOutlineIcon from "@/assets/icons/trash-outline.svg?react";
import TrashSlashIcon from "@/assets/icons/trash-slash.svg?react";
import EditOutlineIcon from "@/assets/icons/edit-outline.svg?react";
import { ActionData } from "@/types/tableAction";
import { CountryBadge } from "../shared/badge/country.badge";
import { format, formatDate } from "date-fns";
import { fr } from "date-fns/locale";
import { EllipsisVertical } from "lucide-react";
import { Button } from "../ui/button";
import clsx from "clsx";
import { formatDateWithTimezone } from "@/lib/dates.util";
import { DeliveryStatusEnum } from "@/constants/enums/deliveryStatus.enum";
import { DeliveryStatusBadge } from "../shared/badge/deliveryStatus.badge";
import { getExpeditionIdentifier } from "@/lib/expedition.util";

const columnHelper = createColumnHelper<GetAllExpeditionResponseDtoExpedition>();

type Props = {
  setActionData: (actionData: ActionData) => void;
  navigate: (path: string) => void;
};

export const validatedExpeditionsTableColumnsMobile = ({
  setActionData,
}: Props): ColumnDef<GetAllExpeditionResponseDtoExpedition>[] => {
  return [
    columnHelper.display({
      id: "identifier",
      header: "Expédition",
      cell: (info) => {
        const client =
          info.row.original.deliveryInfo.company ||
          (info.row.original.deliveryInfo.firstName && info.row.original.deliveryInfo.lastName
            ? info.row.original.deliveryInfo.firstName +
              " " +
              info.row.original.deliveryInfo.lastName
            : "-");

        const pickupDate = info.row.original.pickupInfo?.pickupDate;
        const skipPickup = info.row.original.pickupInfo?.skipPickup;
        const canPostpone =
          pickupDate &&
          new Date(pickupDate) > new Date() &&
          info.row.original.deliveryStatus === "WAITING";

        return (
          <button
            onClick={() =>
              setActionData({
                expedition: info.row.original,
                action: "MOBILE",
              })
            }
            className="flex w-full flex-row items-center justify-between"
          >
            <div className="flex flex-1 flex-col gap-1">
              <div className="flex flex-row items-center gap-2">
                <p className="text-sm font-medium text-gray-900">{client}</p>
                <p className="text-xs font-normal text-gray-600">
                  {formatDate(info.row.original.createdAt, "dd MMM yyyy", {
                    locale: fr,
                  })}
                </p>
              </div>
              <div className="flex flex-row items-center gap-2">
                <p className="text-sm text-gray-700">
                  {getExpeditionIdentifier(info.row.original)}
                </p>
                <CountryBadge
                  country={info.row.original.deliveryInfo?.country || "-"}
                  className=""
                />
                <DeliveryStatusBadge
                  type={info.row.original.deliveryStatus as DeliveryStatusEnum}
                  isBo={false}
                />
              </div>
              <div className="flex w-full flex-row items-center gap-2">
                <p className="max-w-[200px] overflow-hidden text-left text-xs font-medium text-ellipsis text-gray-600">
                  Date d'enlèvement
                </p>
                {skipPickup ? (
                  <span className="w-fit text-left font-medium">-</span>
                ) : pickupDate ? (
                  canPostpone ? (
                    <span className="justify-left flex w-fit text-left text-sm font-medium text-blue-700 hover:text-blue-500">
                      {format(pickupDate, "dd MMM yyyy", {
                        locale: fr,
                      })}
                    </span>
                  ) : (
                    <span className="w-fit text-left font-medium">
                      {format(pickupDate, "dd MMM yyyy", {
                        locale: fr,
                      })}
                    </span>
                  )
                ) : (
                  <div className="justify-left flex w-fit">
                    <div className="py-.5 w-fit rounded-md bg-gray-500 px-2">
                      <span className="w-full text-center text-sm font-medium text-white">
                        Non définie
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <EllipsisVertical className="h-5 w-5 text-gray-600" />
          </button>
        );
      },
    }),
  ];
};

export const validatedExpeditionsTableColumns = ({
  setActionData,
  navigate,
}: Props): ColumnDef<GetAllExpeditionResponseDtoExpedition>[] => {
  return [
    columnHelper.display({
      id: "identifier",
      header: "N° expédition",
      cell: (info) => (
        <div className="max-w-[300px] truncate text-sm">
          {getExpeditionIdentifier(info.row.original)}
        </div>
      ),
    }),
    columnHelper.display({
      id: "client",
      header: "Client",
      cell: (info) => {
        const client =
          info.row.original.deliveryInfo.company ||
          (info.row.original.deliveryInfo.firstName && info.row.original.deliveryInfo.lastName
            ? info.row.original.deliveryInfo.firstName +
              " " +
              info.row.original.deliveryInfo.lastName
            : "-");
        return <div className="max-w-[300px] truncate text-sm font-bold">{client}</div>;
      },
    }),
    columnHelper.display({
      id: "country",
      header: "Pays de destination",
      cell: (info) => <CountryBadge country={info.row.original.deliveryInfo?.country || "-"} />,
    }),
    {
      id: "pickupDate",
      accessorKey: "pickupDate",
      header: "Enlèvement le",
      sortDescFirst: true,
      cell: (info) => {
        const pickupDate = info.row.original.pickupInfo?.pickupDate;
        const skipPickup = info.row.original.pickupInfo?.skipPickup;
        const canPostpone =
          pickupDate &&
          new Date(pickupDate) > new Date() &&
          info.row.original.deliveryStatus === "WAITING";

        if (skipPickup) {
          return <span className="w-full text-left font-medium">-</span>;
        } else if (canPostpone) {
          return (
            <button
              className="justify-left flex w-full text-blue-700 hover:text-blue-500"
              onClick={() => {
                setActionData({
                  expedition: info.row.original,
                  action: "POSTPONE",
                });
              }}
            >
              <span className="w-full text-left font-medium">
                {format(pickupDate, "dd/MM/yyyy")}
              </span>
            </button>
          );
        } else if (pickupDate) {
          return (
            <span className="w-full text-left font-medium">{format(pickupDate, "dd/MM/yyyy")}</span>
          );
        } else {
          return (
            <div className="justify-left flex w-full">
              <div className="w-fit rounded-md bg-gray-500 px-3 py-1">
                <span className="w-full text-center text-sm font-medium text-white">
                  Non définie
                </span>
              </div>
            </div>
          );
        }
      },
    },
    columnHelper.display({
      id: "delieveryStatus",
      header: "Statut",
      enableSorting: false,
      cell: (info) => (
        <DeliveryStatusBadge
          type={info.row.original.deliveryStatus as DeliveryStatusEnum}
          isBo={false}
        />
      ),
    }),
    {
      id: "deliveryDate",
      header: "Livré le",
      accessorKey: "deliveryDate",
      sortDescFirst: true,
      cell: (info) => {
        if (!info.row.original.deliveryDateString || !info.row.original.deliveryDate) {
          return (
            <div className="w-fit rounded-md bg-gray-500 px-3 py-1">
              <span className="w-full text-center text-sm font-medium text-white">Non définie</span>
            </div>
          );
        }

        const { date, time, timezone } = formatDateWithTimezone(
          info.row.original.deliveryDateString,
        );

        return (
          <div className="flex w-fit flex-col items-center gap-0">
            <p className="text-sm text-gray-900">{date}</p>
            <p className="text-sm text-gray-600">
              à {time} (utc {timezone})
            </p>
          </div>
        );
      },
    },
    columnHelper.display({
      id: "createdBy",
      header: "Utilisateur",
      cell: (info) => (
        <div className="max-w-[300px] truncate text-sm">
          {info.row.original.createdBy
            ? info.row.original.createdBy.firstName + " " + info.row.original.createdBy.lastName
            : "-"}
        </div>
      ),
    }),
    columnHelper.display({
      id: "actionOne",
      header: "",
      cell: (info) => {
        const row = info.row.original;
        const deliveryStatus = row.deliveryStatus as DeliveryStatusEnum;
        const isDisabled =
          deliveryStatus === DeliveryStatusEnum.FAILED ||
          deliveryStatus === DeliveryStatusEnum.DELIVERED;

        return (
          <div className="flex h-[44px] flex-row items-center justify-center gap-3">
            {row.deletedRequestAt ? (
              <TrashSlashIcon
                className="cursor-pointer text-slate-600 hover:text-slate-800"
                width={20}
                height={20}
                onClick={() => {
                  setActionData({
                    expedition: row,
                    action: "DELETE",
                  });
                }}
              />
            ) : (
              <TrashOutlineIcon
                className={clsx(
                  isDisabled
                    ? "cursor-not-allowed text-slate-400"
                    : "cursor-pointer text-slate-600 hover:text-slate-800",
                )}
                onClick={() => {
                  if (isDisabled) {
                    return;
                  }
                  setActionData({
                    expedition: row,
                    action: "DELETE",
                  });
                }}
              />
            )}
            <EditOutlineIcon
              className={clsx(
                isDisabled
                  ? "cursor-not-allowed text-slate-400"
                  : "cursor-pointer text-slate-600 hover:text-slate-800",
              )}
              onClick={() => {
                if (isDisabled) {
                  return;
                }
                if (deliveryStatus === DeliveryStatusEnum.ONGOING) {
                  navigate(`/update-request/${row.id}`);
                } else if (deliveryStatus === DeliveryStatusEnum.WAITING) {
                  if (row.quotation) {
                    if (row.quotation.viaImportedInvoice) {
                      navigate(`/update-cotation/ocr/${row.id}`);
                    } else {
                      navigate(`/update-cotation/${row.id}`);
                    }
                  } else {
                    navigate(`/update-request/${row.id}`);
                  }
                }
              }}
            />
            <Button
              variant="link"
              type="button"
              className="text-sm"
              onClick={() => {
                setActionData({
                  expedition: row,
                  action: "DETAIL",
                });
              }}
            >
              Détails
            </Button>
          </div>
        );
      },
    }),
  ];
};
